import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Button,
} from '@mui/material';
import { format, isPast, isToday } from 'date-fns';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import Assignments from './Assignments';
import FieldOpsToday from '../components/FieldOpsToday';
import { useNavigate } from 'react-router-dom';
import { getMyTasks, getAwaitingApproval, getMyAssignments, updateTaskStatus, approveTask } from '../services/api';

const TaskItem = ({ task, showProject = true, onStatusChange, onApprove, showActions = false }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isOverdue = task.due_date && isPast(new Date(task.due_date)) && !isToday(new Date(task.due_date));
  const isDueToday = task.due_date && isToday(new Date(task.due_date));

  const handleToggle = async () => {
    setLoading(true);
    try {
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      await onStatusChange(task.id, newStatus);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      await onApprove(task.id);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ListItem
        button
        onClick={() => navigate(`/projects/${task.project_id}`)}
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          borderLeft: isOverdue ? '4px solid' : isDueToday ? '4px solid' : 'none',
          borderColor: isOverdue ? 'error.main' : 'warning.main',
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body1" fontWeight="medium">
                {task.name}
              </Typography>
              {isOverdue && <Chip label="Overdue" size="small" color="error" />}
              {isDueToday && <Chip label="Due Today" size="small" color="warning" />}
              {task.status === 'completed' && <Chip label="Completed" size="small" color="success" />}
            </Box>
          }
          secondary={
            <Box sx={{ mt: 0.5 }}>
              {showProject && (
                <Typography variant="body2" color="text.secondary">
                  {task.project_name} • {task.phase_name}
                </Typography>
              )}
              {task.client_name && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Client: {task.client_name}
                </Typography>
              )}
              {task.due_date && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Due: {format(new Date(task.due_date), 'MMM d, yyyy')}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block">
                Role: {task.raci_type === 'R' ? 'Responsible' : task.raci_type === 'A' ? 'Accountable' : task.raci_type}
              </Typography>
            </Box>
          }
        />
        {showActions && task.status !== 'completed' && (
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            disabled={loading}
          >
            Mark Complete
          </Button>
        )}
        {showActions && task.status === 'completed' && task.requires_approval && !task.approved_by && (
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={(e) => {
              e.stopPropagation();
              handleApprove();
            }}
            disabled={loading}
          >
            Approve
          </Button>
        )}
      </ListItem>
      <Divider component="li" />
    </>
  );
};

const Tasks = () => {
  const navigate = useNavigate();
  // Default to the field-ops queue — "PMs assign, techs execute."
  // Tom Clancy 2026-05-24.
  const [viewMode, setViewMode] = useState('field');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [myTasks, setMyTasks] = useState([]);
  const [awaitingApproval, setAwaitingApproval] = useState([]);
  // Unified "things assigned to me" feed (work items + install tasks + dispatch).
  // Tom Clancy 2026-05-14.
  const [myAssignments, setMyAssignments] = useState([]);
  const [myAssignmentsCounts, setMyAssignmentsCounts] = useState({ work_items: 0, install_tasks: 0, dispatch: 0 });

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const [tasksRes, approvalRes, assignmentsRes] = await Promise.all([
        getMyTasks({ raci_type: 'R' }),
        getAwaitingApproval(),
        getMyAssignments().catch(() => ({ data: { assignments: [], counts: {} } })),
      ]);

      setMyTasks(tasksRes.data);
      setAwaitingApproval(approvalRes.data);
      setMyAssignments(assignmentsRes.data?.assignments || []);
      setMyAssignmentsCounts(assignmentsRes.data?.counts || { work_items: 0, install_tasks: 0, dispatch: 0 });
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await updateTaskStatus(taskId, newStatus);
      await loadTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleTaskApprove = async (taskId) => {
    try {
      await approveTask(taskId);
      await loadTasks();
    } catch (error) {
      console.error('Failed to approve task:', error);
    }
  };

  const pendingTasks = myTasks.filter((t) => t.status !== 'completed');
  const completedTasks = myTasks.filter((t) => t.status === 'completed');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">My Work</Typography>
          <Typography variant="body1" color="text.secondary">
            {viewMode === 'field'
              ? "Installation tasks assigned to you, grouped by project"
              : viewMode === 'tasks'
              ? 'RACI tasks where you are responsible or accountable'
              : 'Work items and assignments across your projects'}
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => { if (v) setViewMode(v); }}
          size="small"
          sx={{ ml: 'auto', '& .MuiToggleButton-root': { textTransform: 'none', px: 2 } }}
        >
          <ToggleButton value="field">Field Work</ToggleButton>
          <ToggleButton value="tasks">RACI Tasks</ToggleButton>
          <ToggleButton value="assignments">Other Assignments</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'field' ? (
        <FieldOpsToday />
      ) : viewMode === 'assignments' ? (
        <Assignments embedded />
      ) : (
      <Paper elevation={2}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label={`Pending (${pendingTasks.length})`} />
          <Tab label={`Awaiting Approval (${awaitingApproval.length})`} />
          <Tab label={`Completed (${completedTasks.length})`} />
          <Tab label={`Assigned to Me (${myAssignments.length})`} />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {activeTab === 0 && (
            <List>
              {pendingTasks.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No pending tasks</Typography>
                </Box>
              ) : (
                pendingTasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onStatusChange={handleTaskStatusChange}
                    onApprove={handleTaskApprove}
                    showActions={true}
                  />
                ))
              )}
            </List>
          )}

          {activeTab === 1 && (
            <List>
              {awaitingApproval.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No tasks awaiting approval</Typography>
                </Box>
              ) : (
                awaitingApproval.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onStatusChange={handleTaskStatusChange}
                    onApprove={handleTaskApprove}
                    showActions={true}
                  />
                ))
              )}
            </List>
          )}

          {activeTab === 2 && (
            <List>
              {completedTasks.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No completed tasks</Typography>
                </Box>
              ) : (
                completedTasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onStatusChange={handleTaskStatusChange}
                    onApprove={handleTaskApprove}
                  />
                ))
              )}
            </List>
          )}

          {activeTab === 3 && (
            <Box>
              {myAssignments.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">Nothing assigned to you right now.</Typography>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, px: 2, pt: 2 }}>
                    {myAssignmentsCounts.dispatch > 0 && (
                      <Chip size="small" label={`${myAssignmentsCounts.dispatch} dispatch`} sx={{ bgcolor: '#1565C020', color: '#1565C0', fontWeight: 600 }} />
                    )}
                    {myAssignmentsCounts.work_items > 0 && (
                      <Chip size="small" label={`${myAssignmentsCounts.work_items} work items`} sx={{ bgcolor: '#2E7D3220', color: '#2E7D32', fontWeight: 600 }} />
                    )}
                    {myAssignmentsCounts.install_tasks > 0 && (
                      <Chip size="small" label={`${myAssignmentsCounts.install_tasks} installation`} sx={{ bgcolor: '#6A1B9A20', color: '#6A1B9A', fontWeight: 600 }} />
                    )}
                  </Box>
                  <List>
                    {myAssignments.map((a) => {
                      const kindLabel = { dispatch: 'Dispatch', work_item: 'Work Item', install_task: 'Installation' }[a.kind] || a.kind;
                      const kindColor = { dispatch: '#1565C0', work_item: '#2E7D32', install_task: '#6A1B9A' }[a.kind] || '#666';
                      const dueLabel = a.due_date ? format(new Date(a.due_date), 'EEE, MMM d') : null;
                      const overdue  = a.due_date && isPast(new Date(a.due_date)) && !isToday(new Date(a.due_date));
                      return (
                        <Box key={a.id}>
                          <ListItem
                            sx={{ cursor: a.project_id ? 'pointer' : 'default', '&:hover': a.project_id ? { bgcolor: '#f5f5f5' } : {} }}
                            onClick={() => a.project_id && navigate(`/projects/${a.project_id}`)}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                  <Chip size="small" label={kindLabel} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${kindColor}20`, color: kindColor }} />
                                  <Typography variant="body1" fontWeight={600}>{a.title || '—'}</Typography>
                                  {dueLabel && (
                                    <Chip size="small" label={dueLabel} sx={{ height: 20, fontSize: '0.7rem', bgcolor: overdue ? '#ffebee' : (isToday(new Date(a.due_date)) ? '#fff3e0' : '#e8f5e9'), color: overdue ? '#c62828' : (isToday(new Date(a.due_date)) ? '#e65100' : '#2e7d32'), fontWeight: 600 }} />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box sx={{ display: 'flex', gap: 2, mt: 0.5, fontSize: '0.85rem', color: 'text.secondary', flexWrap: 'wrap' }}>
                                  {a.project_name && <span>{a.job_number ? a.job_number + ' · ' : ''}{a.project_name}</span>}
                                  {a.description && <span>· {a.description}</span>}
                                  {a.assigned_by && <span>· by {a.assigned_by}</span>}
                                </Box>
                              }
                            />
                          </ListItem>
                          <Divider />
                        </Box>
                      );
                    })}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>
      )}
    </Box>
  );
};

export default Tasks;
